import type { BufferGeometry } from 'three';
import type { GeneratedModel } from './types';

export interface ExportMesh { positions: Float32Array; indices: Uint32Array | null }
export type ExportRequest = { format: 'stl' | '3mf'; wire: ExportMesh; base: ExportMesh | null };
export type ExportResponse = { type: 'progress'; percent: number } | { type: 'result'; blob: Blob } | { type: 'error'; message: string };

function snapshot(geometry: BufferGeometry): ExportMesh {
  const attribute = geometry.getAttribute('position');
  const positions = new Float32Array(attribute.count * 3);
  for (let i = 0; i < attribute.count; i++) {
    positions[i * 3] = attribute.getX(i);
    positions[i * 3 + 1] = attribute.getY(i);
    positions[i * 3 + 2] = attribute.getZ(i);
  }
  const index = geometry.getIndex();
  return { positions, indices: index ? new Uint32Array(index.array) : null };
}

export function exportModel(model: GeneratedModel, format: 'stl' | '3mf', signal: AbortSignal, onProgress: (percent: number) => void): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new DOMException('导出已取消', 'AbortError')); return; }
    const worker = new Worker(new URL('../exportWorker.ts', import.meta.url), { type: 'module' });
    const cleanup = () => { worker.terminate(); signal.removeEventListener('abort', abort); };
    const fail = (error: Error) => { cleanup(); reject(error); };
    const abort = () => fail(new DOMException('导出已取消', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    worker.onerror = (event) => { event.preventDefault(); fail(new Error(event.message || '导出线程启动失败，请刷新后重试。')); };
    worker.onmessageerror = () => fail(new Error('无法读取导出结果，请重试。'));
    worker.onmessage = (event: MessageEvent<ExportResponse>) => {
      const message = event.data;
      if (message.type === 'progress') onProgress(message.percent);
      else if (message.type === 'error') fail(new Error(message.message));
      else { cleanup(); resolve(message.blob); }
    };
    try {
      // Transfer copies only: preview geometry must remain usable after export.
      const wire = snapshot(format === 'stl' ? model.solidGeometry : model.wireGeometry);
      const base = format === '3mf' && model.baseGeometry ? snapshot(model.baseGeometry) : null;
      const transfers: Transferable[] = [];
      for (const mesh of [wire, base]) if (mesh) {
        transfers.push(mesh.positions.buffer);
        if (mesh.indices) transfers.push(mesh.indices.buffer);
      }
      worker.postMessage({ format, wire, base } satisfies ExportRequest, transfers);
    } catch (error) { fail(error instanceof Error ? error : new Error('导出失败。')); }
  });
}
